import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert,
  ToggleButton, ToggleButtonGroup, TextField, IconButton,
  Select, Menu, MenuItem, FormControl, Pagination, Chip, Button,
  Checkbox, ListItemText, Divider, ListSubheader, Collapse, useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReplayIcon from '@mui/icons-material/Replay';
import ViewListIcon from '@mui/icons-material/ViewList';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HouseIcon from '@mui/icons-material/House';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import PaidIcon from '@mui/icons-material/Paid';
import PlaceIcon from '@mui/icons-material/Place';
import DomainIcon from '@mui/icons-material/Domain';
import { PsfColors } from '../theme';
import {
  fetchTransactions, fetchProjects, exportPsfPdf, Transaction, ProjectSummary,
  MarketFilterParams, PropertyTypeFilter, TenureGroupFilter,
} from '../services/api';
import { recordFilterApplication } from '../services/api';
import { serializePsfFilterHistory } from '../services/filterHistory';
import ReferenceRadiusFilter, { ReferenceRadiusValue } from './ReferenceRadiusFilter';
import { QuestCard, PowerMeter, PillSelect, ToggleChip, RangeStat, rangeIsSet } from './QuestKit';

function MultiPillSelect<T extends string>({
  value, options, onChange, accent, c,
}: {
  value: T[];
  options: Array<{ value: T; label: string; icon?: React.ReactNode }>;
  onChange: (value: T[]) => void;
  accent: string;
  c: PsfColors;
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 0.5 }}>
      {options.map((option) => {
        const selected = option.value === ('__all__' as T) ? value.length === 0 : value.includes(option.value);
        return (
          <Box
            key={option.value}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onClick={() => {
              if (option.value === ('__all__' as T)) onChange([]);
              else onChange(value.includes(option.value) ? value.filter((item) => item !== option.value) : [...value, option.value]);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); if (option.value === ('__all__' as T)) onChange([]); else onChange(value.includes(option.value) ? value.filter((item) => item !== option.value) : [...value, option.value]); }
            }}
            sx={{
              minWidth: 0, px: 0.75, py: 0.55, borderRadius: 1.5, cursor: 'pointer', userSelect: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              border: `1.5px solid ${selected ? accent : c.border}`,
              bgcolor: selected ? `${accent}22` : 'transparent', color: selected ? accent : c.textMuted,
              fontSize: '0.7rem', fontWeight: selected ? 800 : 600, whiteSpace: 'nowrap', transition: 'all 140ms',
              '&:hover': { borderColor: accent }, '& svg': { fontSize: 15 },
            }}
          >
            {option.icon}
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

function DottedFilterGroup({
  active, accent, c, children, sx,
}: {
  active: boolean;
  accent: string;
  c: PsfColors;
  children: React.ReactNode;
  sx?: object;
}) {
  return (
    <Box sx={{
      minWidth: 0,
      p: 0.65,
      borderRadius: 1.5,
      border: `1px dashed ${active ? `${accent}88` : c.border}`,
      bgcolor: active ? `${accent}12` : 'transparent',
      transition: 'background-color 140ms, border-color 140ms',
      ...sx,
    }}>
      {children}
    </Box>
  );
}

type Period = '1Y' | '2Y' | '5Y' | 'custom';
type ViewMode = 'unit' | 'project';

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateRange = (start: string, end: string, today: string) => {
  const normalizedStart = start && start <= today ? start : '';
  const normalizedEnd = end && end <= today ? end : '';
  if (normalizedStart && normalizedEnd && normalizedStart > normalizedEnd) {
    return { start: normalizedStart, end: '' };
  }
  return { start: normalizedStart, end: normalizedEnd };
};

const fmt = (n: number | null | undefined, prefix = '') =>
  n == null
    ? '—'
    : prefix + Number(n).toLocaleString('en-SG', {
        maximumFractionDigits: 0,
      });
const fmtDistance = (n: number | null | undefined) =>
  n == null
    ? '—'
    : Math.round(Number(n)).toLocaleString('en-SG');
const fmtDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' });
};
// Reserved for the temporarily disabled AA/RY + AA column formatters.
// const fmtPct = (n: number | null | undefined) => n == null ? '—' : `${Number(n).toFixed(2)}%`;
const formatProjectName = (value: unknown): string => String(value ?? '').toUpperCase();
const distinctSorted = (values: unknown[]): string[] => Array.from(new Set(
  values
    .filter((value) => value != null && String(value).trim() !== '')
    .map((value) => String(value).trim()),
)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
const tenureSortValue = (value: unknown): number => {
  const text = String(value ?? '').trim();
  if (/freehold/i.test(text)) return Number.POSITIVE_INFINITY;
  const match = text.match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};

const RANGE_FIELDS = [
  { key: 'floor', label: 'Floor level' },
  { key: 'bedroom', label: 'Bedrooms' },
  { key: 'area', label: 'Size (sqft)' },
  { key: 'price', label: 'Price ($)' },
  { key: 'psf', label: 'PSF ($)' },
] as const;
type RangeKey = typeof RANGE_FIELDS[number]['key'];

const UNIT_COLUMNS = [
  { key: 'transactionProjectName', label: 'Project', filterType: 'multi', sortable: true, width: 150 },
  { key: 'transactionAddress', label: 'Address', filterType: 'none', sortable: true, width: 150 },
  { key: 'transactionPostalDistrict', label: 'District', filterType: 'multi', sortable: true, width: 70 },
  { key: 'transactionRegion', label: 'Region', filterType: 'multi', sortable: true, width: 62 },
  { key: 'transactionSubtype', label: 'Type', filterType: 'multi', sortable: true, width: 100 },
  { key: 'transactionTenure', label: 'Tenure', filterType: 'multi', sortable: true, width: 120 },
  { key: 'transactionCompletionYear', label: 'Completed', filterType: 'range', sortable: true, width: 76 },
  { key: 'unitCount', label: 'No. Of Units', filterType: 'range', sortable: true, width: 82 },
  { key: 'transactionSaleType', label: 'Sale Type', filterType: 'multi', sortable: true, width: 84 },
  { key: 'transactionDate', label: 'Date', filterType: 'date', sortable: true, width: 88 },
  { key: 'transactionFloor', label: 'Flr', filterType: 'range', sortable: true, width: 46 },
  { key: 'transactionBedroom', label: 'Br', filterType: 'range', sortable: true, width: 44 },
  { key: 'transactionAreaSqft', label: 'Sqft', filterType: 'range', sortable: true, width: 66 },
  { key: 'transactionPrice', label: 'Price', filterType: 'range', sortable: true, width: 88 },
  { key: 'transactionPsf', label: 'PSF', filterType: 'range', sortable: true, width: 66 },
  { key: 'avgProjectPsf', label: 'Avg PSF', filterType: 'range', sortable: true, width: 100 },
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc (km)', filterType: 'range', sortable: true, width: 130 },
] as const;
type UnitColKey = typeof UNIT_COLUMNS[number]['key'];

const tenureGroupForValue = (value: unknown): 'leasehold' | 'freehold' | null => {
  const text = String(value ?? '').trim();
  if (/freehold/i.test(text)) return 'freehold';
  const years = Number(text.match(/\d+(?:\.\d+)?/)?.[0]);
  return Number.isFinite(years) ? (years <= 103 ? 'leasehold' : 'freehold') : null;
};

const PROJECT_COLUMNS = [
  { key: 'projectName', label: 'Project', numeric: false, filterType: 'multi', width: 180 },
  { key: 'district', label: 'District', numeric: true, filterType: 'multi', width: 90 },
  { key: 'region', label: 'Region', numeric: false, filterType: 'multi', width: 90 },
  { key: 'subtype', label: 'Type', numeric: false, filterType: 'multi', width: 100 },
  { key: 'tenure', label: 'Tenure', numeric: false, filterType: 'multi', width: 120 },
  { key: 'completionYear', label: 'Completed', numeric: true, filterType: 'range', width: 95 },
  { key: 'nearestPriSch', label: 'Nearest Pri Sch', numeric: false, filterType: 'multi', width: 170 },
  { key: 'nearestPriSchDistance', label: 'Pri Sch Distance (m)', numeric: true, filterType: 'range', width: 145 },
  { key: 'nearestMrt', label: 'Nearest MRT', numeric: false, filterType: 'multi', width: 150 },
  { key: 'nearestMrtDistance', label: 'MRT Distance (m)', numeric: true, filterType: 'range', width: 130 },
  { key: 'unitCount', label: 'No. Of Units', numeric: true, filterType: 'range', width: 105 },
  { key: 'txnCount', label: 'No. of Tsn', numeric: true, filterType: 'range', width: 95 },
  { key: 'avgAreaSqft', label: 'Avg Sqft', numeric: true, filterType: 'range', width: 95 },
  { key: 'avgPrice', label: 'Avg Price', numeric: true, filterType: 'range', width: 110 },
  // Temporarily disabled for the UI; retain the backend fields/calculations
  // so these columns can be reinstated without changing the API contract.
  // { key: 'aa', label: 'AA', numeric: true, filterType: 'range', filterDisabled: true, width: 72 },
  // { key: 'ryPlusAa', label: 'RY + AA', numeric: true, filterType: 'range', filterDisabled: true, width: 95 },
  { key: 'distanceFromReferenceKm', label: 'Dist fr Ref. Loc (km)', numeric: true, filterType: 'range', width: 130 },
  { key: 'minPsf', label: 'Min PSF', numeric: true, filterType: 'range', width: 95 },
  { key: 'maxPsf', label: 'Max PSF', numeric: true, filterType: 'range', width: 95 },
  { key: 'avgPsf', label: 'Avg PSF', numeric: true, filterType: 'range', width: 100 },
] as const;
type ProjectColKey = typeof PROJECT_COLUMNS[number]['key'];
export type HeaderRange = { min: string; max: string };

const parseRangeNumber = (value: string): number => parseFloat(value.replace(/,/g, ''));
const formatGroupedNumberInput = (value: string): string => {
  const digits = value.replace(/[^\d]/g, '');
  return digits ? Number(digits).toLocaleString('en-SG') : '';
};

export const calculateColumnWidths = <K extends string>(
  columns: ReadonlyArray<{ key: K; label: string }>,
  rows: unknown[],
  valueFor: (row: any, key: K) => unknown,
): string[] => {
  const weights = columns.map((column) => {
    const contentLength = rows.reduce<number>((longest, row) => Math.max(
      longest,
      String(valueFor(row, column.key) ?? '').length,
    ), column.label.length);
    return Math.max(4, Math.min(18, contentLength));
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  return weights.map((weight) => `${(weight / total) * 100}%`);
};

type MultiSelectProps = {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  c: PsfColors;
  minWidth?: number;
  fullWidth?: boolean;
  size?: 'md' | 'lg' | 'xl';
};

export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  c,
  minWidth = 150,
  fullWidth = false,
  size = 'md',
}: MultiSelectProps) {
  const [search, setSearch] = useState('');
  const scale = size === 'xl' ? 1.22 : size === 'lg' ? 1.1 : 1;

  // Separate UI state from the actual API filter value.
  const [selectAllActive, setSelectAllActive] = useState(false);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return options;

    return options.filter((option) =>
      option.toLowerCase().includes(q)
    );
  }, [options, search]);

  const handleSelectAll = () => {
    if (selectAllActive) {
      // Turn Select All OFF.
      setSelectAllActive(false);

      // Clear actual selections.
      onChange([]);
    } else {
      // Turn Select All ON.
      setSelectAllActive(true);

      // IMPORTANT:
      // Do not send every project/region to the API.
      // [] means no restriction on the backend.
      onChange([]);
    }
  };

  const handleOptionClick = (option: string) => {
    // Once the user manually chooses an option,
    // leave Select All mode.
    setSelectAllActive(false);

    if (value.includes(option)) {
      onChange(
        value.filter((v) => v !== option)
      );
    } else {
      onChange([
        ...value,
        option,
      ]);
    }
  };

  return (
    <FormControl
      size="small"
      fullWidth={fullWidth}
      sx={{ minWidth: fullWidth ? 0 : minWidth }}
    >
      <Select
        multiple
        displayEmpty
        value={value}
        onClose={() => setSearch('')}

        // We manually handle clicks below instead.
        onChange={() => {}}

        renderValue={() => {
          if (selectAllActive) {
            return `All ${label}`;
          }

          if (value.length === 0) {
            return `All ${label}`;
          }

          return `${label}: ${value.length}`;
        }}

        sx={{
          fontSize: `${0.72 * scale}rem`,
          color: c.text,

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: c.border,
          },
        }}

        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 360,
              minWidth: Math.max(
                minWidth,
                220,
              ),
              bgcolor: c.surfaceCard,
              color: c.text,
            },
          },

          MenuListProps: {
            sx: {
              pt: 0,
            },
          },

          disableAutoFocusItem: true,
        }}
      >
        {/* Search */}
        {options.length > 8 && (
          <ListSubheader
            sx={{
              bgcolor: c.surfaceCard,
              pt: 1.5,
              pb: 1,
            }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={`Search ${label.toLowerCase()}...`}
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              onKeyDown={(e) =>
                e.stopPropagation()
              }
              sx={{
                '& input': {
                  color: c.text,
                  fontSize: '0.72rem',
                },

                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: c.border,
                },
              }}
            />
          </ListSubheader>
        )}

        {/* Select All */}
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            handleSelectAll();
          }}
          sx={{
            fontSize: '0.72rem',
            mt:
              options.length > 8
                ? 0.25
                : 0.75,
          }}
        >
          <Checkbox
            size="small"
            checked={selectAllActive}
          />

          <ListItemText
            primary="Select All"
            primaryTypographyProps={{
              fontSize: '0.72rem',
              fontWeight: 700,
            }}
          />
        </MenuItem>

        <Divider
          sx={{
            my: 0.5,
            borderColor: c.border,
          }}
        />

        {filteredOptions.length === 0 ? (
          <MenuItem
            disabled
            sx={{
              fontSize: '0.72rem',
            }}
          >
            No options
          </MenuItem>
        ) : (
          filteredOptions.map(
            (option) => (
              <MenuItem
                key={option}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleOptionClick(
                    option,
                  );
                }}
                sx={{
                  fontSize: '0.72rem',
                }}
              >
                <Checkbox
                  size="small"
                  checked={
                    selectAllActive ||
                    value.includes(option)
                  }
                />

                <ListItemText
                  primary={option}
                  primaryTypographyProps={{
                    fontSize: '0.72rem',
                  }}
                />
              </MenuItem>
            ),
          )
        )}
      </Select>
    </FormControl>
  );
}

function RangeFilter({ label, value, onChange, c }: {
  label: string;
  value: HeaderRange;
  onChange: (value: HeaderRange) => void;
  c: PsfColors;
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.65rem', display: 'block', mb: 0.25 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <TextField size="small" type="number" placeholder="Min" value={value.min}
          onChange={(e) => onChange({ ...value, min: e.target.value })}
          sx={{ width: 88, '& input': { color: c.text, fontSize: '0.72rem', py: 0.55 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }} />
        <TextField size="small" type="number" placeholder="Max" value={value.max}
          onChange={(e) => onChange({ ...value, max: e.target.value })}
          sx={{ width: 88, '& input': { color: c.text, fontSize: '0.72rem', py: 0.55 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }} />
      </Box>
    </Box>
  );
}

export function HeaderMenu({
  filterType, options, multiValue, rangeValue, onMultiChange, onRangeChange,
  onSort, sortable, sortByThisColumn, sortDir, defaultSortActive, filterDisabled = false, c,
  groupThousands = false, leftDate, rightDate, onLeftDateChange, onRightDateChange,
}: {
  filterType: 'multi' | 'range' | 'none' | 'date';
  options: string[];
  multiValue: string[];
  rangeValue: HeaderRange;
  onMultiChange: (value: string[]) => void;
  onRangeChange: (value: HeaderRange) => void;
  onSort: (dir: 'default' | 'asc' | 'desc') => void;
  sortable: boolean;
  sortByThisColumn: boolean;
  sortDir: 'asc' | 'desc';
  defaultSortActive: boolean;
  filterDisabled?: boolean;
  c: PsfColors;
  groupThousands?: boolean;
  /** The left input is the inclusive lower bound (afterDate). */
  leftDate?: string;
  /** The right input is the inclusive upper bound (beforeDate). */
  rightDate?: string;
  onLeftDateChange?: (value: string) => void;
  onRightDateChange?: (value: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterSearch, setFilterSearch] = useState('');
  // Keep selected values visible while deriving new options from the current
  // base search result set.
  const menuOptions = useMemo(
    () => Array.from(new Set([...options, ...multiValue])).filter((option) => option.trim() !== ''),
    [options, multiValue],
  );
  const filteredOptions = useMemo(() => {
    const q = filterSearch.trim().toLowerCase();
    if (!q) return menuOptions;
    return menuOptions.filter((option) => option.toLowerCase().includes(q));
  }, [menuOptions, filterSearch]);
  const hasFilter = multiValue.length > 0 || Boolean(rangeValue.min || rangeValue.max || leftDate || rightDate);
  // A column is active when it has a filter or an explicit sort. The default
  // ordering is not treated as an active user selection.
  // The default table ordering is itself an active visual state for the
  // default column (Date in Unit View, No. of Tsn in Project View). Explicit
  // sorts remain active on whichever column was selected.
  const active = hasFilter || sortByThisColumn;

  const sortButtonSx = {
    flex: 1,
    py: 0.75,
    px: 1,
    fontSize: '0.7rem',
    color: c.textMuted,
    borderColor: c.border,
    '&.Mui-selected': { bgcolor: c.orange, color: '#fff', '&:hover': { bgcolor: c.orange } },
  } as const;

  return (
    <>
      <IconButton
        size="small"
        aria-label="Sort and filter column"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{ color: active ? c.orange : 'rgba(255,255,255,0.72)', p: 0.25, ml: 0.25 }}
      >
        <MenuIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => { setAnchorEl(null); setFilterSearch(''); }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { bgcolor: c.surfaceCard, color: c.text, width: 270, maxHeight: 480, p: 1.25 } }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Typography sx={{ color: '#2196f3', fontSize: '0.78rem', mb: 0.75 }}>Sort</Typography>
        <ToggleButtonGroup exclusive fullWidth size="small" sx={{ mb: 1.5 }}>
          <ToggleButton value="default" selected={defaultSortActive} onClick={() => onSort('default')} sx={sortButtonSx}>Default</ToggleButton>
          <ToggleButton value="asc" disabled={!sortable} selected={!defaultSortActive && sortByThisColumn && sortDir === 'asc'} onClick={() => onSort('asc')} sx={sortButtonSx}>Asc</ToggleButton>
          <ToggleButton value="desc" disabled={!sortable} selected={!defaultSortActive && sortByThisColumn && sortDir === 'desc'} onClick={() => onSort('desc')} sx={sortButtonSx}>Desc</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <Typography sx={{ color: '#2196f3', fontSize: '0.78rem' }}>Filter</Typography>
          <FilterListIcon sx={{ color: '#2196f3', fontSize: 17 }} />
        </Box>

        {filterDisabled || filterType === 'none' ? (
          <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', py: 1 }}>
            {filterDisabled ? 'Percentage data is not available yet' : 'No value filter for this column'}
          </Typography>
        ) : filterType === 'date' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <TextField size="small" type="date" value={leftDate || ''}
              inputProps={{ max: rightDate || undefined }}
              onChange={(e) => onLeftDateChange?.(e.target.value)}
              sx={{ width: 132, '& input': { color: c.text, fontSize: '0.72rem', py: 0.6 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }} />
            <Typography sx={{ color: c.textMuted, fontSize: '1rem' }}>–</Typography>
            <TextField size="small" type="date" value={rightDate || ''}
              inputProps={{ min: leftDate || undefined }}
              onChange={(e) => onRightDateChange?.(e.target.value)}
              sx={{ width: 132, '& input': { color: c.text, fontSize: '0.72rem', py: 0.6 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }} />
          </Box>
        ) : filterType === 'multi' ? (
          <Box>
            {menuOptions.length > 8 && (
              <TextField
                fullWidth
                size="small"
                placeholder="Search..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  mt: 0.5,
                  mb: 1,
                  '& input': { color: c.text, fontSize: '0.72rem' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
                }}
              />
            )}
            <MenuItem
              onClick={() => onMultiChange(menuOptions.length > 0 && menuOptions.every((option) => multiValue.includes(option)) ? [] : [...menuOptions])}
              sx={{ fontSize: '0.72rem', borderRadius: 1, px: 0.5, mb: 0.5 }}
            >
              <Checkbox
                size="small"
                checked={menuOptions.length > 0 && menuOptions.every((option) => multiValue.includes(option))}
                indeterminate={multiValue.length > 0 && !(menuOptions.length > 0 && menuOptions.every((option) => multiValue.includes(option)))}
              />
              <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: '0.72rem', fontWeight: 700 }} />
            </MenuItem>
            <Divider sx={{ mb: 0.5, borderColor: c.border }} />
            <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <Typography sx={{ px: 1, py: 1, fontSize: '0.72rem', color: c.textMuted }}>No options</Typography>
              ) : filteredOptions.map((option) => (
                <MenuItem
                  key={option}
                  onClick={() => onMultiChange(multiValue.includes(option) ? multiValue.filter((v) => v !== option) : [...multiValue, option])}
                  sx={{ fontSize: '0.72rem', borderRadius: 1, px: 0.5 }}
                >
                  <Checkbox size="small" checked={multiValue.includes(option)} />
                  <ListItemText primary={option} primaryTypographyProps={{ fontSize: '0.72rem', noWrap: true }} />
                </MenuItem>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              type={groupThousands ? 'text' : 'number'}
              inputProps={groupThousands ? { inputMode: 'numeric' } : undefined}
              placeholder="Min"
              value={rangeValue.min}
              onChange={(e) => onRangeChange({ ...rangeValue, min: groupThousands ? formatGroupedNumberInput(e.target.value) : e.target.value })}
              sx={{ '& input': { color: c.text, fontSize: '0.75rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }}
            />
            <TextField
              fullWidth
              size="small"
              type={groupThousands ? 'text' : 'number'}
              inputProps={groupThousands ? { inputMode: 'numeric' } : undefined}
              placeholder="Max"
              value={rangeValue.max}
              onChange={(e) => onRangeChange({ ...rangeValue, max: groupThousands ? formatGroupedNumberInput(e.target.value) : e.target.value })}
              sx={{ '& input': { color: c.text, fontSize: '0.75rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }}
            />
          </Box>
        )}

        <Button
          fullWidth
          size="small"
          disabled={!hasFilter}
          onClick={() => {
            if (filterType === 'multi') onMultiChange([]);
            else if (filterType === 'date') { onLeftDateChange?.(''); onRightDateChange?.(''); }
            else onRangeChange({ min: '', max: '' });
          }}
          sx={{ mt: 1.25, color: hasFilter ? c.textMuted : `${c.textMuted}66`, textTransform: 'uppercase', fontSize: '0.68rem' }}
        >
          Clear Filter
        </Button>
      </Menu>
    </>
  );
}

interface CompareViewProps {
  c: PsfColors;
  onReset: () => void;
  reportContext?: { userInfo: { Name?: string; Mobile?: string; Email?: string; CEANumber?: string; Photo?: string } | null; consumerName?: string | null };
}

export default function CompareView({ c, onReset, reportContext }: CompareViewProps) {
  const C = c;
  const [viewMode, setViewMode] = useState<ViewMode>('unit');
  const isCompactViewport = useMediaQuery('(max-width:1279px), (max-height:950px)');
  const [filtersOpen, setFiltersOpen] = useState(true);

  // ── Shared market filters ────────────────────────────────────────────────
  const [period, setPeriod] = useState<Period>('1Y');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [propertyType, setPropertyType] = useState<PropertyTypeFilter>('all');
  const [tenureGroup, setTenureGroup] = useState<TenureGroupFilter>('all');
  const [saleTypes, setSaleTypes] = useState<string[]>([]);
  const [excludeExecutiveCondominiums, setExcludeExecutiveCondominiums] = useState(false);
  const [referenceRadius, setReferenceRadius] = useState<ReferenceRadiusValue | null>(null);
  const [referenceResetKey, setReferenceResetKey] = useState(0);
  const [rangeInputs, setRangeInputs] = useState<Record<string, string>>({});
  const [appliedRanges, setAppliedRanges] = useState<Record<string, string>>({});
  const [leftDate, setLeftDate] = useState('');
  const [rightDate, setRightDate] = useState('');
  const [unitSearchMulti, setUnitSearchMulti] = useState<{ project: string[]; district: string[]; planArea: string[]; region: string[] }>({ project: [], district: [], planArea: [], region: [] });
  const [projectSearchMulti, setProjectSearchMulti] = useState<{ project: string[]; district: string[]; planArea: string[]; region: string[] }>({ project: [], district: [], planArea: [], region: [] });
  const [appliedPeriod, setAppliedPeriod] = useState<Period>('1Y');
  const [appliedCustomStart, setAppliedCustomStart] = useState('');
  const [appliedCustomEnd, setAppliedCustomEnd] = useState('');
  const [appliedPropertyType, setAppliedPropertyType] = useState<PropertyTypeFilter>('all');
  const [appliedTenureGroup, setAppliedTenureGroup] = useState<TenureGroupFilter>('all');
  const [appliedSaleTypes, setAppliedSaleTypes] = useState<string[]>([]);
  const [appliedExcludeExecutiveCondominiums, setAppliedExcludeExecutiveCondominiums] = useState(false);
  const [appliedReferenceRadius, setAppliedReferenceRadius] = useState<ReferenceRadiusValue | null>(null);
  const [appliedUnitSearchMulti, setAppliedUnitSearchMulti] = useState(unitSearchMulti);
  const [appliedProjectSearchMulti, setAppliedProjectSearchMulti] = useState(projectSearchMulti);
  const [appliedLeftDate, setAppliedLeftDate] = useState('');
  const [appliedRightDate, setAppliedRightDate] = useState('');
  const todayDate = formatLocalDate(new Date());
  const startDateMax = rightDate
    ? (rightDate < todayDate ? rightDate : todayDate)
    : todayDate;
  const endDateMin = leftDate || undefined;

  const handleStartDateChange = (value: string) => {
    if (!value) { setLeftDate(''); return; }
    if (value > todayDate || value > startDateMax) return;
    setLeftDate(value);
  };

  const handleEndDateChange = (value: string) => {
    if (!value) { setRightDate(''); return; }
    if (value > todayDate || (endDateMin && value < endDateMin)) return;
    setRightDate(value);
  };
  const [startupUnitOptions, setStartupUnitOptions] = useState<{ project: string[]; district: string[]; planArea: string[]; region: string[] }>({ project: [], district: [], planArea: [], region: [] });
  const [startupProjectOptions, setStartupProjectOptions] = useState<{ project: string[]; district: string[]; planArea: string[]; region: string[] }>({ project: [], district: [], planArea: [], region: [] });
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [visibleUnitCols, setVisibleUnitCols] = useState<Set<UnitColKey>>(
    () => new Set(UNIT_COLUMNS.map((col) => col.key)),
  );
  const [visibleProjectCols, setVisibleProjectCols] = useState<Set<ProjectColKey>>(
    () => new Set(PROJECT_COLUMNS.map((col) => col.key)),
  );
  const applyFilters = useCallback(() => {
    const normalizedDates = normalizeDateRange(leftDate, rightDate, todayDate);
    if (normalizedDates.start !== leftDate) setLeftDate(normalizedDates.start);
    if (normalizedDates.end !== rightDate) setRightDate(normalizedDates.end);
    const activeSearch = viewMode === 'unit' ? unitSearchMulti : projectSearchMulti;
    const filterHistory = serializePsfFilterHistory({
      period,
      customStart,
      customEnd,
      propertyType,
      tenure: tenureGroup,
      saleTypes,
      excludeExecutiveCondominiums,
      projects: activeSearch.project,
      districts: activeSearch.district,
      planAreas: activeSearch.planArea,
      regions: activeSearch.region,
      reference: referenceRadius,
      ranges: rangeInputs,
      dateFrom: normalizedDates.start,
      dateTo: normalizedDates.end,
    });
    setAppliedPeriod(period);
    setAppliedCustomStart(customStart);
    setAppliedCustomEnd(customEnd);
    setAppliedPropertyType(propertyType);
    setAppliedTenureGroup(tenureGroup);
    setAppliedSaleTypes(saleTypes);
    setAppliedExcludeExecutiveCondominiums(excludeExecutiveCondominiums);
    setAppliedReferenceRadius(referenceRadius);
    setAppliedUnitSearchMulti(unitSearchMulti);
    setAppliedProjectSearchMulti(projectSearchMulti);
    setAppliedRanges(rangeInputs);
    setAppliedLeftDate(normalizedDates.start);
    setAppliedRightDate(normalizedDates.end);
    setUnitAppliedHeaderMulti({});
    setUnitAppliedHeaderRanges({});
    setProjAppliedHeaderMulti({});
    setProjAppliedHeaderRanges({});
    setUnitHeaderMulti({});
    setUnitHeaderRanges({});
    setProjHeaderMulti({});
    setProjHeaderRanges({});
    setPage(1);
    setProjPage(1);
    if (isCompactViewport) setFiltersOpen(false);
    void recordFilterApplication('COMPARE_TRANSACTIONS', filterHistory).catch(() => undefined);
  }, [period, customStart, customEnd, propertyType, tenureGroup, saleTypes,
    excludeExecutiveCondominiums, referenceRadius, unitSearchMulti, projectSearchMulti,
    rangeInputs, leftDate, rightDate, todayDate, viewMode, isCompactViewport]);

  const clearFilters = useCallback(() => {
    const emptySearch = { project: [], district: [], planArea: [], region: [] };
    setPeriod('1Y');
    setCustomStart('');
    setCustomEnd('');
    setPropertyType('all');
    setTenureGroup('all');
    setSaleTypes([]);
    setExcludeExecutiveCondominiums(false);
    setReferenceRadius(null);
    setRangeInputs({});
    setLeftDate('');
    setRightDate('');
    setUnitSearchMulti(emptySearch);
    setProjectSearchMulti(emptySearch);
    setUnitHeaderMulti({});
    setUnitHeaderRanges({});
    setProjHeaderMulti({});
    setProjHeaderRanges({});
    setReferenceResetKey((key) => key + 1);
  }, []);

  const activeRangeCount = useMemo(
    () => RANGE_FIELDS.filter((f) => rangeInputs[`${f.key}Min`] || rangeInputs[`${f.key}Max`]).length,
    [rangeInputs],
  );

  const rangeValue = (key: RangeKey) => ({
    min: rangeInputs[`${key}Min`] || '',
    max: rangeInputs[`${key}Max`] || '',
  });
  const setRangeValue = (key: RangeKey, value: { min: string; max: string }) => {
    setRangeInputs((previous) => ({
      ...previous,
      [`${key}Min`]: value.min,
      [`${key}Max`]: value.max,
    }));
    setPage(1);
    setProjPage(1);
  };
  const anyRangeSet = (keys: RangeKey[]) => keys.some((key) => rangeIsSet(rangeValue(key)));
  const marketActive = period !== '1Y' || propertyType !== 'all' || tenureGroup !== 'all'
    || saleTypes.length > 0 || excludeExecutiveCondominiums;
  const budgetActive = anyRangeSet(['price', 'psf', 'area']);
  const locationActive = Boolean(
    (viewMode === 'unit' ? unitSearchMulti : projectSearchMulti).project.length
    || (viewMode === 'unit' ? unitSearchMulti : projectSearchMulti).district.length
    || (viewMode === 'unit' ? unitSearchMulti : projectSearchMulti).region.length
    || referenceRadius,
  );
  const transactionActive = anyRangeSet(['floor', 'bedroom']) || Boolean(leftDate || rightDate);
  const activeSearch = viewMode === 'unit' ? unitSearchMulti : projectSearchMulti;
  // Count concrete user-selected controls, not their parent QuestCard. A
  // project selection and Exclude E.C therefore contribute exactly two.
  const criteriaCount = Number(period !== '1Y')
    + Number(propertyType !== 'all')
    + Number(tenureGroup !== 'all')
    + Number(saleTypes.length > 0)
    + Number(excludeExecutiveCondominiums)
    + activeRangeCount
    + Number(activeSearch.project.length > 0)
    + Number(activeSearch.district.length > 0)
    + Number(activeSearch.region.length > 0)
    + Number(Boolean(referenceRadius))
    + Number(Boolean(leftDate || rightDate));

  const handleReferenceRadiusChange = useCallback((value: ReferenceRadiusValue | null) => {
    setReferenceRadius(value);
  }, []);

  const marketParams = useCallback((): MarketFilterParams => {
    const params: MarketFilterParams = {
      propertyType: appliedPropertyType,
      tenureGroup: appliedTenureGroup,
      excludeExecutiveCondominiums: appliedExcludeExecutiveCondominiums,
      saleTypes: appliedSaleTypes,
    };
    if (appliedPeriod === 'custom' && appliedCustomStart && appliedCustomEnd) {
      params.startDate = appliedCustomStart;
      params.endDate = appliedCustomEnd;
    } else if (appliedPeriod !== 'custom') {
      params.period = appliedPeriod;
    }
    if (appliedReferenceRadius) {
      params.referenceLatitude = appliedReferenceRadius.latitude;
      params.referenceLongitude = appliedReferenceRadius.longitude;
      params.radiusKm = appliedReferenceRadius.radiusKm;
    }
    RANGE_FIELDS.forEach((f) => {
      const min = parseRangeNumber(appliedRanges[`${f.key}Min`] || '');
      const max = parseRangeNumber(appliedRanges[`${f.key}Max`] || '');
      if (Number.isFinite(min)) (params as any)[`${f.key}Min`] = min;
      if (Number.isFinite(max)) (params as any)[`${f.key}Max`] = max;
    });
    return params;
  }, [appliedPeriod, appliedCustomStart, appliedCustomEnd, appliedPropertyType, appliedTenureGroup,
    appliedExcludeExecutiveCondominiums, appliedSaleTypes, appliedRanges, appliedReferenceRadius]);

  useEffect(() => {
    let mounted = true;
    const unique = (values: unknown[]): string[] => Array.from(
      new Set<string>(values.filter((v) => v != null && String(v).trim() !== '').map((v) => String(v))),
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    // Project metadata is the most reliable source for the startup Project / Region
    // dropdowns and also provides District / Plan Area for Project View.
    fetchProjects({ propertyType: 'all', tenureGroup: 'all', period: '1Y', limit: 5000 } as any)
      .then((projectRes) => {
        if (!mounted) return;
        const project = unique(projectRes.data.map((r: ProjectSummary) => formatProjectName(r.projectName)));
        const region = unique(projectRes.data.map((r: ProjectSummary) => r.region));
        const district = unique(projectRes.data.map((r: ProjectSummary) => r.district));
        const planArea = unique(projectRes.data.map((r: ProjectSummary) => r.planArea));
        setStartupUnitOptions({ project, district, planArea, region });
        setStartupProjectOptions({
          project,
          district,
          planArea,
          region,
        });
      })
      .catch(() => { /* primary table loaders surface fetch errors */ });

    return () => { mounted = false; };
  }, []);

  // ── Unit view state ──────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [unitSortMode, setUnitSortMode] = useState<'default' | 'explicit'>('default');
  const [unitHeaderMulti, setUnitHeaderMulti] = useState<Partial<Record<UnitColKey, string[]>>>({});
  const [unitHeaderRanges, setUnitHeaderRanges] = useState<Partial<Record<UnitColKey, HeaderRange>>>({});
  const [unitAppliedHeaderMulti, setUnitAppliedHeaderMulti] = useState<Partial<Record<UnitColKey, string[]>>>({});
  const [unitAppliedHeaderRanges, setUnitAppliedHeaderRanges] = useState<Partial<Record<UnitColKey, HeaderRange>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Transaction[]>([]);
  const [unitDistinctValues, setUnitDistinctValues] = useState<Partial<Record<UnitColKey, string[]>>>({});
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
  // Date header inputs are positional: left is the lower/after bound,
  // right is the upper/before bound. Keeping these names explicit avoids
  // accidentally reversing the controls when building API requests.
  const unitRequestVersion = useRef(0);
  const unitDistinctContextRef = useRef<string | null>(null);


  const loadUnits = useCallback(async () => {
    const requestVersion = ++unitRequestVersion.current;
    setLoading(true);
    setError(null);
    const market = marketParams();
    // Distinct header values describe the base search result set. Include all
    // search-level inputs in the cache key so changing them cannot reuse a
    // stale option list from a previous query.
    const distinctContextKey = JSON.stringify({
      market,
      projects: appliedUnitSearchMulti.project,
      districts: appliedUnitSearchMulti.district,
      regions: appliedUnitSearchMulti.region,
      leftDate: appliedLeftDate,
      rightDate: appliedRightDate,
    });

    if (appliedLeftDate && appliedRightDate && appliedLeftDate > appliedRightDate) {
      // Invalidate any previous response so it cannot restore an old total.
      setError('The left date must be on or before the right date.');
      if (requestVersion === unitRequestVersion.current) setLoading(false);
      return;
    }

    try {
      const res = await fetchTransactions({
        ...market,

        page,
        pageSize,
        sortBy,
        sortDir,
        // Distinct header values are expensive (they scan the full filtered
        // set). Load them once per base-search context, not every time Unit
        // View is reopened or page 1 is requested.
        includeDistincts: unitDistinctContextRef.current !== distinctContextKey,
        headerMulti: unitAppliedHeaderMulti as Record<string, string[]>,
        headerRanges: unitAppliedHeaderRanges as Record<string, HeaderRange>,
        beforeDate: appliedRightDate || undefined,
        afterDate: appliedLeftDate || undefined,

        projects:
          appliedUnitSearchMulti.project.length
            ? appliedUnitSearchMulti.project
            : undefined,

        districts: appliedUnitSearchMulti.district.length ? appliedUnitSearchMulti.district : undefined,
        regions:
          appliedUnitSearchMulti.region.length
            ? appliedUnitSearchMulti.region
            : undefined,
      });

      if (requestVersion !== unitRequestVersion.current) return;
      setData(res.data);
      setTotal(res.total);
      if (res.distinctValues) {
        const distinct = { ...res.distinctValues } as Record<string, string[]>;
        distinct.transactionProjectName = (distinct.transactionProjectName || []).map(formatProjectName);
        setUnitDistinctValues(distinct as Partial<Record<UnitColKey, string[]>>);
        unitDistinctContextRef.current = distinctContextKey;
      }
      setDateRange({ start: res.startDate, end: res.endDate });
    } catch (e: any) {
      if (requestVersion === unitRequestVersion.current) setError(e.message || 'Failed to load data');
    } finally {
      if (requestVersion === unitRequestVersion.current) setLoading(false);
    }
  }, [
    marketParams,
    page,
    pageSize,
    sortBy,
    sortDir,
    appliedUnitSearchMulti,
    unitAppliedHeaderMulti,
    unitAppliedHeaderRanges,
    appliedLeftDate,
    appliedRightDate,
  ]);

  useEffect(() => {
    if (viewMode === 'unit') loadUnits();
  }, [viewMode, loadUnits]);

  // ── Project view state ───────────────────────────────────────────────────
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projLoading, setProjLoading] = useState(false);
  const [projError, setProjError] = useState<string | null>(null);
  const [projSortBy, setProjSortBy] = useState<ProjectColKey>('txnCount');
  const [projSortDir, setProjSortDir] = useState<'asc' | 'desc'>('desc');
  const [projSortMode, setProjSortMode] = useState<'default' | 'explicit'>('default');
  const [projHeaderMulti, setProjHeaderMulti] = useState<Partial<Record<ProjectColKey, string[]>>>({});
  const [projHeaderRanges, setProjHeaderRanges] = useState<Partial<Record<ProjectColKey, HeaderRange>>>({});
  const [projAppliedHeaderMulti, setProjAppliedHeaderMulti] = useState<Partial<Record<ProjectColKey, string[]>>>({});
  const [projAppliedHeaderRanges, setProjAppliedHeaderRanges] = useState<Partial<Record<ProjectColKey, HeaderRange>>>({});
  const [projPage, setProjPage] = useState(1);
  const [projPageSize, setProjPageSize] = useState(20);


  const loadProjects = useCallback(async () => {
    setProjLoading(true);
    setProjError(null);
    try {
      const res = await fetchProjects({
        ...marketParams(),
        projects: appliedProjectSearchMulti.project.length ? appliedProjectSearchMulti.project : undefined,
        districts: appliedProjectSearchMulti.district.length ? appliedProjectSearchMulti.district : undefined,
        regions: appliedProjectSearchMulti.region.length ? appliedProjectSearchMulti.region : undefined,
        limit: 3000,
      });
      setProjects(res.data);
      setDateRange({ start: res.startDate, end: res.endDate });
    } catch (e: any) {
      setProjError(e.message || 'Failed to load projects');
    } finally {
      setProjLoading(false);
    }
  }, [marketParams, appliedProjectSearchMulti]);

  useEffect(() => {
    if (viewMode === 'project') loadProjects();
  }, [viewMode, loadProjects]);

  const searchedProjects = useMemo(() => projects.filter((r) => {
    if (appliedProjectSearchMulti.project.length && !appliedProjectSearchMulti.project.includes(String(r.projectName))) return false;
    if (appliedProjectSearchMulti.district.length && !appliedProjectSearchMulti.district.includes(String(r.district ?? ''))) return false;
    if (appliedProjectSearchMulti.region.length && !appliedProjectSearchMulti.region.includes(String(r.region ?? ''))) return false;
    return true;
  }), [projects, appliedProjectSearchMulti]);

  const projectDistinctValues = useMemo(() => {
    const result: Partial<Record<ProjectColKey, string[]>> = {};
    PROJECT_COLUMNS.forEach((col) => {
      result[col.key] = distinctSorted(searchedProjects.map((row) => {
        const value = (row as any)[col.key];
        return col.key === 'projectName' ? formatProjectName(value) : value;
      }));
    });
    result.tenure = ['<= 103 yrs (Leasehold)', '> 103 yrs (999 yrs / Freehold)'];
    return result;
  }, [searchedProjects]);

  const visibleProjects = useMemo(() => {
    let rows = searchedProjects.filter((r) => {
      for (const [key, vals] of Object.entries(projAppliedHeaderMulti) as [string, string[] | undefined][]) {
        if (!vals?.length) continue;
        if (key === 'tenure') {
          const group = tenureGroupForValue(r.tenure);
          if (!group || !vals.some((value) => value.toLowerCase().includes(group))) return false;
        } else if (!vals.includes(String((r as any)[key] ?? ''))) return false;
      }
      for (const [key, range] of Object.entries(projAppliedHeaderRanges) as [string, HeaderRange | undefined][]) {
        if (!range?.min && !range?.max) continue;
        const n = Number((r as any)[key]);
        if (!Number.isFinite(n)) return false;
        if (range.min && n < parseRangeNumber(range.min)) return false;
        if (range.max && n > parseRangeNumber(range.max)) return false;
      }
      return true;
    });
    const col = PROJECT_COLUMNS.find((cdef) => cdef.key === projSortBy);
    const dir = projSortDir === 'asc' ? 1 : -1;
    const sortedRows = rows.map((row, index) => ({ row, index })).sort((a, b) => {
      const avRow = a.row;
      const bvRow = b.row;
      const av = (avRow as any)[projSortBy];
      const bv = (bvRow as any)[projSortBy];
      if (av == null && bv == null) return a.index - b.index;
      if (av == null) return 1;
      if (bv == null) return -1;
      let comparison = 0;
      if (projSortBy === 'tenure') comparison = tenureSortValue(av) - tenureSortValue(bv);
      else if (col?.numeric) comparison = Number(av) - Number(bv);
      else comparison = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
      return comparison === 0 ? a.index - b.index : comparison * dir;
    }).map(({ row }) => row);
    return sortedRows;
  }, [searchedProjects, projAppliedHeaderMulti, projAppliedHeaderRanges, projSortBy, projSortDir]);

  const projTotalPages = Math.max(1, Math.ceil(visibleProjects.length / projPageSize));
  const pagedProjects = useMemo(
    () => visibleProjects.slice((projPage - 1) * projPageSize, projPage * projPageSize),
    [visibleProjects, projPage, projPageSize],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleUnitSort = (col: string, dir: 'default' | 'asc' | 'desc') => {
    if (dir === 'default') { setSortBy('transactionDate'); setSortDir('desc'); setUnitSortMode('default'); }
    else { setSortBy(col); setSortDir(dir); setUnitSortMode('explicit'); }
    setPage(1);
  };

  const handleProjSort = (col: ProjectColKey, dir: 'default' | 'asc' | 'desc') => {
    if (dir === 'default') { setProjSortBy('txnCount'); setProjSortDir('desc'); setProjSortMode('default'); }
    else { setProjSortBy(col); setProjSortDir(dir); setProjSortMode('explicit'); }
    setProjPage(1);
  };

  const handleViewChange = (nextView: ViewMode) => {
    if (nextView === viewMode) return;
    const unitToProject: Partial<Record<UnitColKey, ProjectColKey>> = {
      transactionProjectName: 'projectName', transactionPostalDistrict: 'district',
      transactionRegion: 'region', transactionSubtype: 'subtype', transactionTenure: 'tenure',
      transactionCompletionYear: 'completionYear', unitCount: 'unitCount',
      avgProjectPsf: 'avgPsf', distanceFromReferenceKm: 'distanceFromReferenceKm',
    };
    const unitHasFilters = Boolean(
      unitSearchMulti.project.length || unitSearchMulti.district.length || unitSearchMulti.region.length
      || Object.values(unitHeaderMulti).some((values) => values?.length)
      || Object.values(unitHeaderRanges).some((range) => range?.min || range?.max)
      || leftDate || rightDate,
    );
    const projectHasFilters = Boolean(
      projectSearchMulti.project.length || projectSearchMulti.district.length || projectSearchMulti.region.length
      || Object.values(projHeaderMulti).some((values) => values?.length)
      || Object.values(projHeaderRanges).some((range) => range?.min || range?.max),
    );
    if (nextView === 'project') {
      setProjectSearchMulti((previous) => ({
        ...previous,
        project: unitSearchMulti.project,
        district: unitSearchMulti.district,
        region: unitSearchMulti.region,
      }));
      setProjHeaderMulti((previous) => {
        const next = { ...previous };
        Object.entries(unitToProject).forEach(([unitKey, projectKey]) => {
          const value = unitHeaderMulti[unitKey as UnitColKey];
          if (value?.length) next[projectKey!] = unitKey === 'transactionPostalDistrict'
            ? value.map((district) => district.padStart(2, '0'))
            : value;
          else delete next[projectKey!];
        });
        return next;
      });
      setProjHeaderRanges((previous) => {
        const next = { ...previous };
        Object.entries(unitToProject).forEach(([unitKey, projectKey]) => {
          const value = unitHeaderRanges[unitKey as UnitColKey];
          if (value && (value.min || value.max)) next[projectKey!] = { ...value };
          else delete next[projectKey!];
        });
        return next;
      });
      const mappedSort = unitToProject[sortBy as UnitColKey];
      if (!unitHasFilters) {
        setProjSortBy('txnCount'); setProjSortDir('desc'); setProjSortMode('default');
      } else if (unitSortMode === 'explicit' && mappedSort) {
        setProjSortBy(mappedSort); setProjSortDir(sortDir); setProjSortMode('explicit');
      }
      setProjPage(1);
    } else {
      setUnitSearchMulti((previous) => ({
        ...previous,
        project: projectSearchMulti.project,
        district: projectSearchMulti.district,
        region: projectSearchMulti.region,
      }));
      const projectToUnit = Object.fromEntries(Object.entries(unitToProject).map(([unit, project]) => [project, unit]));
      setUnitHeaderMulti((previous) => {
        const next = { ...previous };
        Object.entries(projectToUnit).forEach(([projectKey, unitKey]) => {
          const value = projHeaderMulti[projectKey as ProjectColKey];
          if (value?.length) next[unitKey as UnitColKey] = projectKey === 'district'
            ? value.map((district) => district.padStart(2, '0'))
            : value;
          else delete next[unitKey as UnitColKey];
        });
        return next;
      });
      setUnitHeaderRanges((previous) => {
        const next = { ...previous };
        Object.entries(projectToUnit).forEach(([projectKey, unitKey]) => {
          const value = projHeaderRanges[projectKey as ProjectColKey];
          if (value && (value.min || value.max)) next[unitKey as UnitColKey] = { ...value };
          else delete next[unitKey as UnitColKey];
        });
        return next;
      });
      const mappedSort = Object.fromEntries(Object.entries(unitToProject).map(([unit, project]) => [project, unit]))[projSortBy] as UnitColKey | undefined;
      if (!projectHasFilters) {
        setSortBy('transactionDate'); setSortDir('desc'); setUnitSortMode('default');
      } else if (projSortMode === 'explicit' && mappedSort) {
        setSortBy(mappedSort); setSortDir(projSortDir); setUnitSortMode('explicit');
      }
      setPage(1);
    }
    setViewMode(nextView);
  };

  const unitCell = (col: UnitColKey, txn: Transaction): React.ReactNode => {
    switch (col) {
      case 'transactionDate': return fmtDate(txn.transactionDate);
      case 'transactionPrice': return fmt(txn.transactionPrice, '$');
      case 'transactionPsf': return <b>{fmt(txn.transactionPsf, '$') as string}</b>;
      case 'avgProjectPsf': return fmt(txn.avgProjectPsf, '$');
      case 'transactionAreaSqft': return txn.transactionAreaSqft ? Math.round(txn.transactionAreaSqft).toLocaleString() : '—';
      case 'unitCount': return txn.unitCount == null ? '—' : txn.unitCount.toLocaleString();
      case 'distanceFromReferenceKm': return txn.distanceFromReferenceKm == null ? '—' : txn.distanceFromReferenceKm.toFixed(2);
      default: {
        const v = (txn as any)[col];
        return v == null ? '—' : col === 'transactionProjectName' ? formatProjectName(v) : v;
      }
    }
  };

  const projectCell = (col: ProjectColKey, project: ProjectSummary): React.ReactNode => {
    switch (col) {
      case 'txnCount': return project.txnCount.toLocaleString();
      case 'avgAreaSqft': return fmt(project.avgAreaSqft);
      case 'avgPrice': return fmt(project.avgPrice, '$');
      case 'minPsf': return fmt(project.minPsf, '$');
      case 'maxPsf': return fmt(project.maxPsf, '$');
      case 'avgPsf': return fmt(project.avgPsf, '$');
      case 'nearestPriSch': return project.nearestPriSch || '—';
      case 'nearestPriSchDistance': return fmtDistance(project.nearestPriSchDistance);
      case 'nearestMrt': return project.nearestMrt || '—';
      case 'nearestMrtDistance': return fmtDistance(project.nearestMrtDistance);
      case 'unitCount': return project.unitCount == null ? '—' : project.unitCount.toLocaleString();
      // AA/RY + AA display is temporarily disabled; restore these cases with
      // the column definitions when the metrics are brought back.
      case 'distanceFromReferenceKm': return project.distanceFromReferenceKm == null ? '—' : project.distanceFromReferenceKm.toFixed(2);
      default: {
        const value = (project as any)[col];
        return value == null ? '—' : col === 'projectName' ? formatProjectName(value) : value;
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedUnitData = data;
  const displayedUnitColumns = UNIT_COLUMNS.filter((col) => visibleUnitCols.has(col.key));
  const displayedProjectColumns = PROJECT_COLUMNS.filter((col) => visibleProjectCols.has(col.key));

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    setExportError(null);
    try {
      const unitValue = (row: Transaction, key: UnitColKey): string => {
        const value = (row as any)[key];
        if (value == null || value === '') return '—';
        if (key === 'transactionDate') return fmtDate(String(value));
        if (key === 'transactionPrice' || key === 'transactionPsf' || key === 'avgProjectPsf') return `$${Number(value).toLocaleString()}`;
        if (key === 'transactionAreaSqft') return Math.round(Number(value)).toLocaleString();
        if (key === 'distanceFromReferenceKm') return Number(value).toFixed(2);
        return String(value);
      };
      const projectValue = (row: ProjectSummary, key: ProjectColKey): string => {
        const value = (row as any)[key];
        // AA/RY + AA export handling is intentionally disabled with the UI.
        if (value == null || value === '') return '—';
        if (['avgPrice', 'minPsf', 'maxPsf', 'avgPsf'].includes(key)) return `$${Number(value).toLocaleString()}`;
        if (key === 'nearestPriSchDistance' || key === 'nearestMrtDistance') return fmtDistance(Number(value));
        if (key === 'distanceFromReferenceKm') return Number(value).toFixed(2);
        if (typeof value === 'number') return Number(value).toLocaleString();
        return String(value);
      };
      const unitMode = viewMode === 'unit';
      const columns = unitMode ? displayedUnitColumns : displayedProjectColumns;
      let exportTransactions: Transaction[] = [];
      if (unitMode) {
        const request = {
          ...marketParams(),
          pageSize: 200,
          sortBy,
          sortDir,
          projects: appliedUnitSearchMulti.project.length ? appliedUnitSearchMulti.project : undefined,
          districts: appliedUnitSearchMulti.district.length ? appliedUnitSearchMulti.district : undefined,
          regions: appliedUnitSearchMulti.region.length ? appliedUnitSearchMulti.region : undefined,
          headerMulti: unitHeaderMulti as Record<string, string[]>,
          headerRanges: unitHeaderRanges as Record<string, HeaderRange>,
          beforeDate: appliedRightDate || undefined,
          afterDate: appliedLeftDate || undefined,
        };
        const first = await fetchTransactions({ ...request, page: 1 });
        exportTransactions = [...first.data];
        const pageCount = Math.ceil(first.total / 200);
        for (let firstPage = 2; firstPage <= pageCount; firstPage += 8) {
          const pageNumbers = Array.from(
            { length: Math.min(8, pageCount - firstPage + 1) },
            (_, index) => firstPage + index,
          );
          const responses = await Promise.all(pageNumbers.map((nextPage) => fetchTransactions({ ...request, page: nextPage })));
          exportTransactions.push(...responses.flatMap((response) => response.data));
        }
        exportTransactions = exportTransactions.filter((row) => {
          for (const [key, values] of Object.entries(unitHeaderMulti) as [UnitColKey, string[]][]) {
            if (!values?.length) continue;
            if (key === 'transactionTenure') {
              const group = tenureGroupForValue(row.transactionTenure);
              if (!group || !values.some((value) => value.toLowerCase().includes(group))) return false;
            } else if (!values.includes(String((row as any)[key] ?? ''))) return false;
          }
          for (const [key, range] of Object.entries(unitHeaderRanges) as [UnitColKey, HeaderRange][]) {
            if (!range?.min && !range?.max) continue;
            const value = Number((row as any)[key]);
            if (!Number.isFinite(value)) return false;
            if (range.min && value < parseRangeNumber(range.min)) return false;
            if (range.max && value > parseRangeNumber(range.max)) return false;
          }
          return true;
        });
      }
      const rows = unitMode
        ? exportTransactions.map((row) => displayedUnitColumns.map((column) => unitValue(row, column.key)))
        : visibleProjects.map((row) => displayedProjectColumns.map((column) => projectValue(row, column.key)));

      const filterSummary: string[] = [];
      const periodLabels: Record<Period, string> = { '1Y': '1 year', '2Y': '2 years', '5Y': '5 years', custom: 'Custom date range' };
      if (appliedPeriod === '1Y' && dateRange) {
        filterSummary.push(`Date range: ${fmtDate(dateRange.start)} – ${fmtDate(dateRange.end)}`);
      } else {
        filterSummary.push(`Period: ${periodLabels[appliedPeriod]}`);
        if (appliedPeriod === 'custom' && appliedCustomStart && appliedCustomEnd) filterSummary.push(`Date range: ${fmtDate(appliedCustomStart)} – ${fmtDate(appliedCustomEnd)}`);
      }
      if (unitMode && appliedLeftDate) filterSummary.push(`Transactions on or after: ${fmtDate(appliedLeftDate)}`);
      if (unitMode && appliedRightDate) filterSummary.push(`Transactions on or before: ${fmtDate(appliedRightDate)}`);
      if (appliedPropertyType !== 'all') filterSummary.push(`Property type: ${appliedPropertyType === 'condo' ? 'Condo / Apartment' : 'Landed'}`);
      if (appliedTenureGroup !== 'all') filterSummary.push(`Tenure: ${appliedTenureGroup === 'leasehold' ? 'Leasehold (≤103 years)' : '999-year / Freehold'}`);
      if (appliedSaleTypes.length) filterSummary.push(`Sale types: ${appliedSaleTypes.join(', ')}`);
      if (appliedExcludeExecutiveCondominiums) filterSummary.push('E.C excluded');
      if (appliedReferenceRadius) filterSummary.push(`Reference: ${appliedReferenceRadius.label} · Radius: ${appliedReferenceRadius.radiusKm} km`);
      const searchFilters = unitMode ? appliedUnitSearchMulti : appliedProjectSearchMulti;
      if (searchFilters.project.length) filterSummary.push(`Projects: ${searchFilters.project.join(', ')}`);
      if (searchFilters.district.length) filterSummary.push(`Districts: ${searchFilters.district.join(', ')}`);
      if (searchFilters.region.length) filterSummary.push(`Regions: ${searchFilters.region.join(', ')}`);
      RANGE_FIELDS.forEach((field) => {
        const min = appliedRanges[`${field.key}Min`];
        const max = appliedRanges[`${field.key}Max`];
        if (min || max) filterSummary.push(`${field.label}: ${min ? `min ${min}` : ''}${min && max ? ', ' : ''}${max ? `max ${max}` : ''}`);
      });
      const activeHeaderMulti = unitMode ? unitHeaderMulti : projHeaderMulti;
      const activeHeaderRanges = unitMode ? unitHeaderRanges : projHeaderRanges;
      const columnLabels = new Map<string, string>([...UNIT_COLUMNS, ...PROJECT_COLUMNS].map((column) => [column.key, column.label]));
      Object.entries(activeHeaderMulti).forEach(([key, values]) => {
        if (values?.length) filterSummary.push(`${columnLabels.get(key) || key}: ${values.join(', ')}`);
      });
      Object.entries(activeHeaderRanges).forEach(([key, range]) => {
        if (!range?.min && !range?.max) return;
        filterSummary.push(`${columnLabels.get(key) || key}: ${range.min ? `min ${range.min}` : ''}${range.min && range.max ? ', ' : ''}${range.max ? `max ${range.max}` : ''}`);
      });

      const projectTransactionCount = visibleProjects.reduce((sum, project) => sum + project.txnCount, 0);
      const activeSortKey = unitMode ? sortBy : projSortBy;
      const activeSortDirection = unitMode ? sortDir : projSortDir;
      const activeSortLabel = unitMode
        ? UNIT_COLUMNS.find((column) => column.key === activeSortKey)?.label || activeSortKey
        : PROJECT_COLUMNS.find((column) => column.key === activeSortKey)?.label || activeSortKey;
      const sortDescription = activeSortKey === 'transactionDate'
        ? `${activeSortLabel}: ${activeSortDirection === 'desc' ? 'newest to oldest' : 'oldest to newest'}`
        : `${activeSortLabel}: ${activeSortDirection === 'desc' ? 'descending' : 'ascending'}`;
      await exportPsfPdf({
        userInfo: reportContext?.userInfo || null,
        consumerName: reportContext?.consumerName || undefined,
        reportTitle: 'PERSONALIZED PSF REPORT',
        viewLabel: unitMode ? 'Compare Transactions — Unit View' : 'Compare Transactions — Project View',
        subject: '',
        coverSubject: '',
        columns: columns.map((column) => column.label),
        rows,
        filterSummary,
        resultSummary: unitMode
          ? [
            `Search results: ${exportTransactions.length.toLocaleString()} transactions`,
            `Sorted by ${sortDescription}`,
          ]
          : [
            `Search results: ${visibleProjects.length.toLocaleString()} projects`,
            `Transactions represented: ${projectTransactionCount.toLocaleString()}`,
            `Sorted by ${sortDescription}`,
          ],
        filename: `compare-transactions-${unitMode ? 'units' : 'projects'}-${Date.now()}.pdf`,
      });
    } catch (exportFailure: any) {
      setExportError(`PDF export failed: ${exportFailure?.message || 'Unable to generate the report'}`);
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const selHl = { bgcolor: `${C.orange}22 !important`, color: `${C.orange} !important`, borderColor: `${C.orange} !important` };
  const toggleSx = {
    '& .MuiToggleButton-root': { px: 1.2, py: 0.4, color: C.textMuted, borderColor: C.border, fontSize: '0.72rem', textTransform: 'none' },
    '& .Mui-selected': selHl,
  } as const;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: 2, gap: 1.25, overflow: 'hidden' }}>
      {/* ── Header row ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: C.text, fontSize: '1.05rem' }}>
              Compare Transactions
            </Typography>
            <Chip
              size="small"
              label={viewMode === 'unit' ? `${total.toLocaleString()} transactions` : `${visibleProjects.length.toLocaleString()} projects`}
              sx={{ bgcolor: `${C.navy}22`, color: C.textMuted, fontSize: '0.66rem', height: 20 }}
            />
          </Box>
          {dateRange && (
            <Typography variant="caption" sx={{ color: C.textMuted }}>
              <CalendarTodayIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
              {fmtDate(dateRange.start)} – {fmtDate(dateRange.end)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
            onClick={onReset}
            sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
          >
            Start Over
          </Button>
        </Box>
      </Box>

      {/* ── Search criteria (Benchmark-style grouped filter bar) ── */}
      <Paper elevation={0} sx={{ bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 1.5 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setFiltersOpen((open) => !open); }
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, cursor: 'pointer', userSelect: 'none', borderBottom: filtersOpen ? `1px solid ${C.border}` : 'none' }}
        >
          <FilterAltIcon sx={{ fontSize: 16, color: C.orange }} />
          <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>Search Criteria</Typography>
          {criteriaCount > 0 && <Chip size="small" label={`${criteriaCount} active`} sx={{ bgcolor: `${C.orange}1e`, color: C.orange, fontWeight: 700, fontSize: '0.64rem', height: 18 }} />}
          <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'flex' }, mr: 1 }}><PowerMeter count={criteriaCount} c={C} /></Box>
          <Typography sx={{ color: C.textMuted, fontSize: '0.7rem' }}>{filtersOpen ? 'Hide' : 'Show'}</Typography>
          <ExpandMoreIcon sx={{ fontSize: 18, color: C.textMuted, transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 160ms' }} />
        </Box>
        <Collapse in={filtersOpen} unmountOnExit>
          <Box sx={{ p: 1.1, display: 'grid', gap: 1, alignItems: 'start', alignContent: 'start', gridAutoRows: 'max-content', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <QuestCard step={1} icon={<EventIcon />} title="Market" hint="Time window, property type, tenure & sale mix" accent="#2196f3" active={marketActive} onReset={() => { setPeriod('1Y'); setCustomStart(''); setCustomEnd(''); setPropertyType('all'); setTenureGroup('all'); setSaleTypes([]); setExcludeExecutiveCondominiums(false); }} c={C}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <DottedFilterGroup active={period !== '1Y' || Boolean(customStart || customEnd)} accent="#2196f3" c={C}>
                  <PillSelect<Period> value={period} onChange={(value) => { setPeriod(value); setPage(1); setProjPage(1); }} options={[{ value: '1Y', label: '1 Year (Default)' }, { value: '2Y', label: '2 Years' }, { value: '5Y', label: '5 Years' }, { value: 'custom', label: 'Custom' }]} accent="#2196f3" c={C} />
                  {period === 'custom' && <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.6 }}><TextField type="date" size="small" value={customStart} onChange={(e) => setCustomStart(e.target.value)} sx={{ flex: 1, '& input': { color: C.text, fontSize: '0.72rem', py: 0.6 } }} /><Typography sx={{ color: C.textMuted }}>–</Typography><TextField type="date" size="small" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} sx={{ flex: 1, '& input': { color: C.text, fontSize: '0.72rem', py: 0.6 } }} /></Box>}
                </DottedFilterGroup>
                <DottedFilterGroup active={propertyType !== 'all'} accent="#2196f3" c={C}>
                  <PillSelect<PropertyTypeFilter>
                  value={propertyType}
                  onChange={(value) => { setPropertyType(value); setPage(1); setProjPage(1); }}
                  options={[
                    { value: 'all', label: 'All', icon: <AllInclusiveIcon /> },
                    { value: 'condo', label: 'Condo / Apartment', icon: <ApartmentIcon /> },
                    { value: 'landed', label: 'Landed', icon: <HouseIcon /> },
                  ]}
                  accent="#2196f3"
                  c={C}
                  />
                </DottedFilterGroup>
                <DottedFilterGroup active={tenureGroup !== 'all'} accent="#2196f3" c={C}>
                  <PillSelect<TenureGroupFilter>
                  value={tenureGroup}
                  onChange={(value) => { setTenureGroup(value); setPage(1); setProjPage(1); }}
                  options={[
                    { value: 'all', label: 'All', icon: <AllInclusiveIcon /> },
                    { value: 'leasehold', label: 'Leasehold' },
                    { value: 'freehold', label: '999-year / Freehold' },
                  ]}
                  accent="#2196f3"
                  c={C}
                  />
                </DottedFilterGroup>
                <DottedFilterGroup active={saleTypes.length > 0} accent="#2196f3" c={C}>
                  <MultiPillSelect
                  value={saleTypes}
                  onChange={(value) => { setSaleTypes(value); setPage(1); setProjPage(1); }}
                  options={[
                    { value: '__all__', label: 'All', icon: <AllInclusiveIcon /> },
                    { value: 'New Sale', label: 'New Sale' },
                    { value: 'Resale', label: 'Resale' },
                    { value: 'Sub Sale', label: 'Sub Sale' },
                  ]}
                  accent="#2196f3"
                  c={C}
                  />
                </DottedFilterGroup>
                <DottedFilterGroup active={excludeExecutiveCondominiums} accent="#2196f3" c={C} sx={{ display: 'inline-flex', alignSelf: 'flex-start' }}>
                  <ToggleChip label="Exclude E.C" icon={<DomainIcon />} on={excludeExecutiveCondominiums} onToggle={() => setExcludeExecutiveCondominiums((value) => !value)} accent="#2196f3" c={C} />
                </DottedFilterGroup>
              </Box>
            </QuestCard>

            <QuestCard step={2} icon={<PaidIcon />} title="Budget & Size" hint="Tap a quick pick or type your own range" badge="MOST USED" accent="#f47920" active={budgetActive} onReset={() => setRangeInputs((previous) => { const next = { ...previous }; ['price', 'psf', 'area'].forEach((key) => { delete next[`${key}Min`]; delete next[`${key}Max`]; }); return next; })} c={C}>
              <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', display: 'grid', gap: 0.75, justifyContent: 'center', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
                <RangeStat
                  label="Avg Price ($)"
                  money
                  value={rangeValue('price')}
                  onChange={(value) => setRangeValue('price', value)}
                  presets={[
                    { label: '< $1M', max: '1,000,000' },
                    { label: '$1–1.5M', min: '1,000,000', max: '1,500,000' },
                    { label: '$1.5–2M', min: '1,500,000', max: '2,000,000' },
                    { label: '$2–3M', min: '2,000,000', max: '3,000,000' },
                    { label: '$3M+', min: '3,000,000' },
                  ]}
                  accent="#f47920"
                  c={C}
                  emphasis
                />
                <RangeStat
                  label="Avg Sqft"
                  value={rangeValue('area')}
                  onChange={(value) => setRangeValue('area', value)}
                  presets={[
                    { label: '< 600', max: '600' },
                    { label: '600–900', min: '600', max: '900' },
                    { label: '900–1,200', min: '900', max: '1,200' },
                    { label: '1,200+', min: '1,200' },
                  ]}
                  accent="#f47920"
                  c={C}
                  emphasis
                />
                <RangeStat
                  label="Avg PSF ($)"
                  money
                  value={rangeValue('psf')}
                  onChange={(value) => setRangeValue('psf', value)}
                  presets={[
                    { label: '< $1.5k', max: '1,500' },
                    { label: '$1.5–2k', min: '1,500', max: '2,000' },
                    { label: '$2–2.5k', min: '2,000', max: '2,500' },
                    { label: '$2.5k+', min: '2,500' },
                  ]}
                  accent="#f47920"
                  c={C}
                  emphasis
                />
              </Box>
            </QuestCard>

            <QuestCard step={3} icon={<PlaceIcon />} title="Location" hint="Projects, districts, regions and reference radius" accent="#16a085" active={locationActive} onReset={() => { const clear = { project: [], district: [], planArea: [], region: [] }; setUnitSearchMulti(clear); setProjectSearchMulti(clear); setReferenceRadius(null); setReferenceResetKey((key) => key + 1); }} c={C}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {viewMode === 'unit'
                  ? <>
                    <DottedFilterGroup active={unitSearchMulti.project.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Projects" options={startupUnitOptions.project} value={unitSearchMulti.project} onChange={(value) => setUnitSearchMulti((previous) => ({ ...previous, project: value }))} c={C} fullWidth /></DottedFilterGroup>
                    <DottedFilterGroup active={unitSearchMulti.district.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Districts" options={startupUnitOptions.district} value={unitSearchMulti.district} onChange={(value) => setUnitSearchMulti((previous) => ({ ...previous, district: value }))} c={C} fullWidth /></DottedFilterGroup>
                    <DottedFilterGroup active={unitSearchMulti.region.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Regions" options={startupUnitOptions.region} value={unitSearchMulti.region} onChange={(value) => setUnitSearchMulti((previous) => ({ ...previous, region: value }))} c={C} fullWidth /></DottedFilterGroup>
                  </>
                  : <>
                    <DottedFilterGroup active={projectSearchMulti.project.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Projects" options={startupProjectOptions.project} value={projectSearchMulti.project} onChange={(value) => setProjectSearchMulti((previous) => ({ ...previous, project: value }))} c={C} fullWidth /></DottedFilterGroup>
                    <DottedFilterGroup active={projectSearchMulti.district.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Districts" options={startupProjectOptions.district} value={projectSearchMulti.district} onChange={(value) => setProjectSearchMulti((previous) => ({ ...previous, district: value }))} c={C} fullWidth /></DottedFilterGroup>
                    <DottedFilterGroup active={projectSearchMulti.region.length > 0} accent="#16a085" c={C}><MultiSelectFilter label="Regions" options={startupProjectOptions.region} value={projectSearchMulti.region} onChange={(value) => setProjectSearchMulti((previous) => ({ ...previous, region: value }))} c={C} fullWidth /></DottedFilterGroup>
                  </>}
                <DottedFilterGroup active={Boolean(referenceRadius)} accent="#16a085" c={C}>
                  <ReferenceRadiusFilter c={C} resetKey={referenceResetKey} onChange={handleReferenceRadiusChange} />
                </DottedFilterGroup>
              </Box>
            </QuestCard>

            <QuestCard step={4} icon={<DomainIcon />} title="Transaction Details" hint="Unit-level dates, floors and bedrooms" accent="#7e57c2" active={transactionActive} onReset={() => { setLeftDate(''); setRightDate(''); setRangeInputs((previous) => { const next = { ...previous }; ['floor', 'bedroom'].forEach((key) => { delete next[`${key}Min`]; delete next[`${key}Max`]; }); return next; }); }} c={C}>
              <Box sx={{ display: 'grid', gap: 0.75 }}>
                <DottedFilterGroup active={Boolean(leftDate || rightDate)} accent="#7e57c2" c={C}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}><TextField type="date" size="small" value={leftDate} onChange={(e) => handleStartDateChange(e.target.value)} inputProps={{ max: startDateMax }} sx={{ flex: 1, '& input': { color: C.text, fontSize: '0.72rem', py: 0.6 } }} /><Typography sx={{ color: C.textMuted }}>–</Typography><TextField type="date" size="small" value={rightDate} onChange={(e) => handleEndDateChange(e.target.value)} inputProps={{ min: endDateMin, max: todayDate }} sx={{ flex: 1, '& input': { color: C.text, fontSize: '0.72rem', py: 0.6 } }} /></Box>
                </DottedFilterGroup>
                <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                  <RangeStat
                    label="Floor level"
                    value={rangeValue('floor')}
                    onChange={(value) => setRangeValue('floor', value)}
                    presets={[
                      { label: 'Low (1–5)', min: '1', max: '5' },
                      { label: 'Mid (6–15)', min: '6', max: '15' },
                      { label: 'High (16+)', min: '16' },
                    ]}
                    accent="#7e57c2"
                    c={C}
                    emphasis
                  />
                  <RangeStat
                    label="Bedrooms"
                    value={rangeValue('bedroom')}
                    onChange={(value) => setRangeValue('bedroom', value)}
                    presets={[
                      { label: '1 BR', min: '1', max: '1' },
                      { label: '2 BR', min: '2', max: '2' },
                      { label: '3 BR', min: '3', max: '3' },
                      { label: '4+ BR', min: '4' },
                    ]}
                    accent="#7e57c2"
                    c={C}
                    emphasis
                  />
                </Box>
              </Box>
            </QuestCard>

            <Box sx={{ gridColumn: { sm: 'span 2' }, display: 'flex', justifyContent: 'flex-end', gap: 0.75, flexWrap: 'wrap' }}>
              <Button size="small" onClick={clearFilters} sx={{ color: C.textMuted, border: `1px solid ${C.border}`, textTransform: 'none' }}>Clear Filters</Button>
              <Button size="small" variant="contained" onClick={applyFilters} sx={{ bgcolor: C.orange, textTransform: 'none', fontWeight: 700 }}>Apply Filters</Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => { if (value) handleViewChange(value); }}
          size="small"
          sx={toggleSx}
        >
          <ToggleButton value="unit"><ViewListIcon sx={{ fontSize: 15, mr: 0.5 }} />Unit View</ToggleButton>
          <ToggleButton value="project"><ApartmentIcon sx={{ fontSize: 15, mr: 0.5 }} />Project View</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            size="small"
            startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => void handleExportPdf()}
            disabled={exportingPdf || (viewMode === 'unit'
              ? pagedUnitData.length === 0 || displayedUnitColumns.length === 0
              : visibleProjects.length === 0 || displayedProjectColumns.length === 0)}
            sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
          >
            {exportingPdf ? 'Exporting…' : 'Export PDF'}
          </Button>
          <Button
            size="small"
            startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
            onClick={(event) => setColumnsAnchor(event.currentTarget)}
            sx={{ color: C.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${C.border}`, borderRadius: 1.5, px: 1.2 }}
          >
            Columns
          </Button>
        </Box>
        <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
          <Box sx={{ minWidth: 220, px: 1.5, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Show Columns</Typography>
            <Button
              size="small"
              onClick={() => viewMode === 'unit'
                ? setVisibleUnitCols(new Set(UNIT_COLUMNS.map((col) => col.key)))
                : setVisibleProjectCols(new Set(PROJECT_COLUMNS.map((col) => col.key)))}
            >
              Reset
            </Button>
          </Box>
          <Divider />
          {(viewMode === 'unit' ? UNIT_COLUMNS : PROJECT_COLUMNS).map((col) => {
            const checked = viewMode === 'unit'
              ? visibleUnitCols.has(col.key as UnitColKey)
              : visibleProjectCols.has(col.key as ProjectColKey);
            return (
              <MenuItem key={col.key} dense onClick={() => {
                if (viewMode === 'unit') {
                  setVisibleUnitCols((previous) => {
                    const next = new Set(previous);
                    if (next.has(col.key as UnitColKey)) next.delete(col.key as UnitColKey);
                    else next.add(col.key as UnitColKey);
                    return next;
                  });
                } else {
                  setVisibleProjectCols((previous) => {
                    const next = new Set(previous);
                    if (next.has(col.key as ProjectColKey)) next.delete(col.key as ProjectColKey);
                    else next.add(col.key as ProjectColKey);
                    return next;
                  });
                }
              }}>
                <Checkbox size="small" checked={checked} />
                <ListItemText primary={col.label} primaryTypographyProps={{ fontSize: '0.78rem' }} />
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      {(error || projError || exportError) && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>{exportError || error || projError}</Alert>}

      {/* ── Unit view table ── */}
      {viewMode === 'unit' && (
        <>
          <TableContainer component={Paper} sx={{ flex: 1, bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 1.5, overflow: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1500 }}>
              <TableHead>
                <TableRow>
                  {displayedUnitColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        bgcolor: C.navyDark, color: C.headerText, fontWeight: 700,
                        borderBottom: '2px solid rgba(255,255,255,0.15)',
                        p: '6px 8px', fontSize: '0.72rem', whiteSpace: 'nowrap', minWidth: 72,
                        ...(col.key === 'transactionProjectName' ? { position: 'sticky', left: 0, zIndex: 3 } : {}),
                      }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, whiteSpace: 'nowrap' }}>
                      <span>{col.label}</span>
                      <HeaderMenu
                        filterType={col.filterType}
                        options={unitDistinctValues[col.key] || []}
                        multiValue={unitHeaderMulti[col.key] || []}
                        rangeValue={unitHeaderRanges[col.key] || { min: '', max: '' }}
                        onMultiChange={(value) => {
                          setUnitHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setUnitAppliedHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setPage(1);
                        }}
                        onRangeChange={(value) => {
                          setUnitHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setUnitAppliedHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setPage(1);
                        }}
                        onSort={(dir) => handleUnitSort(col.key, dir)}
                        sortable={col.sortable}
                        sortByThisColumn={sortBy === col.key}
                        sortDir={sortDir}
                        defaultSortActive={unitSortMode === 'default'}
                        filterDisabled={Boolean('filterDisabled' in col && col.filterDisabled)}
                        groupThousands={['transactionPrice', 'transactionPsf', 'avgProjectPsf'].includes(col.key)}
                        leftDate={col.key === 'transactionDate' ? leftDate : undefined}
                        rightDate={col.key === 'transactionDate' ? rightDate : undefined}
                        onLeftDateChange={col.key === 'transactionDate' ? (value) => { setLeftDate(value); setPage(1); setTotal(0); } : undefined}
                        onRightDateChange={col.key === 'transactionDate' ? (value) => { setRightDate(value); setPage(1); setTotal(0); } : undefined}
                        c={C}
                      />
                    </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, displayedUnitColumns.length)} sx={{ textAlign: 'center', py: 6, bgcolor: C.surfaceCard }}>
                      <CircularProgress size={28} sx={{ color: C.orange }} />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, displayedUnitColumns.length)} sx={{ textAlign: 'center', py: 6, color: C.textMuted, bgcolor: C.surfaceCard }}>
                      No transactions found — try widening the filters
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedUnitData.map((txn, idx) => (
                    <TableRow key={txn.transactionId} hover sx={{
                      bgcolor: idx % 2 === 0 ? C.surfaceCard : C.surface,
                      '&:hover': { bgcolor: `${C.navyLight}44` },
                      '& td': { borderBottom: `1px solid ${C.border}`, py: '4px', px: '8px', fontSize: '0.73rem', color: C.text, whiteSpace: 'nowrap' },
                    }}>
                      {displayedUnitColumns.map((col) => (
                        <TableCell key={col.key} sx={col.key === 'transactionProjectName' ? { position: 'sticky', left: 0, zIndex: 2, bgcolor: idx % 2 === 0 ? C.surfaceCard : C.surface, fontWeight: 700, boxShadow: `2px 0 4px ${C.border}`, '&:hover': { bgcolor: `${C.navyLight}44` } } : undefined}>{unitCell(col.key, txn)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, pt: 0.5 }}>
            <Pagination
              count={totalPages}
              page={Math.min(page, totalPages)}
              onChange={(_, v) => setPage(v)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': { color: C.textMuted, borderColor: C.border },
                '& .Mui-selected': { bgcolor: `${C.orange} !important`, color: '#fff !important' },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 84 }}>
              <Select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                sx={{ fontSize: '0.72rem', color: C.text, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
                {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '0.72rem' }}>{n} / pg</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {/* ── Project view table ── */}
      {viewMode === 'project' && (
        <>
          <TableContainer component={Paper} sx={{ flex: 1, bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 1.5, overflow: 'auto' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: displayedProjectColumns.reduce((total, column) => total + column.width, 0),
                tableLayout: 'fixed',
              }}
            >
              <TableHead>
                <TableRow>
                  {displayedProjectColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.numeric ? 'right' : 'left'}
                      sx={{
                        bgcolor: C.navyDark,
                        color: C.headerText,
                        fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', p: '6px 8px',
                        width: col.width, minWidth: col.width, maxWidth: col.width,
                        ...(col.key === 'projectName' ? { position: 'sticky', left: 0, zIndex: 3 } : {}),
                        borderBottom: '2px solid rgba(255,255,255,0.15)',
                      }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: col.numeric ? 'flex-end' : 'flex-start', minWidth: 0, whiteSpace: 'nowrap' }}>
                      <span>{col.label}</span>
                      <HeaderMenu
                        filterType={col.filterType}
                        options={projectDistinctValues[col.key] || []}
                        multiValue={projHeaderMulti[col.key] || []}
                        rangeValue={projHeaderRanges[col.key] || { min: '', max: '' }}
                        onMultiChange={(value) => {
                          setProjHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setProjAppliedHeaderMulti((prev) => ({ ...prev, [col.key]: value }));
                          setProjPage(1);
                        }}
                        onRangeChange={(value) => {
                          setProjHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setProjAppliedHeaderRanges((prev) => ({ ...prev, [col.key]: value }));
                          setProjPage(1);
                        }}
                        onSort={(dir) => handleProjSort(col.key, dir)}
                        sortable
                        sortByThisColumn={projSortBy === col.key}
                        sortDir={projSortDir}
                        defaultSortActive={projSortMode === 'default'}
                        filterDisabled={'filterDisabled' in col ? Boolean(col.filterDisabled) : false}
                        groupThousands={['avgPrice', 'minPsf', 'maxPsf', 'avgPsf'].includes(col.key)}
                        c={C}
                      />
                    </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {projLoading ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, displayedProjectColumns.length)} sx={{ textAlign: 'center', py: 6, bgcolor: C.surfaceCard }}>
                      <CircularProgress size={28} sx={{ color: C.orange }} />
                    </TableCell>
                  </TableRow>
                ) : pagedProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, displayedProjectColumns.length)} sx={{ textAlign: 'center', py: 6, color: C.textMuted, bgcolor: C.surfaceCard }}>
                      No projects found — try widening the filters
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedProjects.map((p, idx) => (
                    <TableRow key={p.projectName} hover sx={{
                      bgcolor: idx % 2 === 0 ? C.surfaceCard : C.surface,
                      '&:hover': { bgcolor: `${C.navyLight}44` },
                          '& td': { borderBottom: `1px solid ${C.border}`, py: '4px', px: '8px', fontSize: '0.73rem', color: C.text, whiteSpace: 'nowrap' },
                    }}>
                      {displayedProjectColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.numeric ? 'right' : 'left'}
                          sx={{
                            width: col.width,
                            minWidth: col.width,
                            maxWidth: col.width,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            ...(col.key === 'projectName'
                              ? { fontWeight: 700, position: 'sticky', left: 0, zIndex: 2, bgcolor: idx % 2 === 0 ? C.surfaceCard : C.surface, boxShadow: `2px 0 4px ${C.border}`, '&:hover': { bgcolor: `${C.navyLight}44` } }
                              : {}),
                          }}
                        >
                          {projectCell(col.key, p)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, pt: 0.5 }}>
            <Pagination
              count={projTotalPages}
              page={Math.min(projPage, projTotalPages)}
              onChange={(_, v) => setProjPage(v)}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': { color: C.textMuted, borderColor: C.border },
                '& .Mui-selected': { bgcolor: `${C.orange} !important`, color: '#fff !important' },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 84 }}>
              <Select value={projPageSize} onChange={(e) => { setProjPageSize(Number(e.target.value)); setProjPage(1); }}
                sx={{ fontSize: '0.72rem', color: C.text, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border } }}>
                {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '0.72rem' }}>{n} / pg</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
}
